import { Box, Center, Flex, Grid, Select, Spinner, Text, useToast, Badge } from '@chakra-ui/react'
import { HeadingPages } from '../../../components/HeadingPages'
import { Card } from './Card'
import { api } from '../../../services/api'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { DragDropContext } from 'react-beautiful-dnd'
import { Tabela } from './Tabela'
import { KanbanBoard } from './KanbanBoard'
import React from 'react'
import { FilterBar } from './FilterBar' // Importação do novo componente

export function ChamadosSolicitados() {
  const [view, setView] = useState('kanban')
  const [filters, setFilters] = useState({ searchTerm: '', responsavel: '' }) // Estado para os filtros
  const toast = useToast()
  const queryClient = useQueryClient()

  // Memoize API query
  const { data, isLoading } = useQuery(
    ['listarChamadosSistema'],
    async () => {
      const response = await api.get('/site/v2/crm-controle/listar-chamados-sistema')
      return response.data
    },
    {
      refetchOnWindowFocus: true,
      staleTime: 1000 * 60, // 1 minuto
    }
  )

  console.log(data)

  useEffect(() => {
    document.title = `${filteredData?.length || 0} Chamados solicitados | Innovation`
  }, [data, filters])

  // Handler para atualização dos filtros
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters)
  }, [])

  // Dados filtrados com useMemo para performance
  const filteredData = useMemo(() => {
    if (!data) return []

    return data.filter((item) => {
      // Filtro por termo de busca (número do chamado ou nome do solicitante)
      const matchesSearchTerm =
        !filters.searchTerm ||
        item.numero_chamado.toString().includes(filters.searchTerm) ||
        item.solicitante?.toLowerCase().includes(filters.searchTerm.toLowerCase())

      // Filtro por responsável
      const matchesResponsavel = !filters.responsavel || item.responsavel === filters.responsavel

      return matchesSearchTerm && matchesResponsavel
    })
  }, [data, filters])

  // Memoize priorityColor function
  const setPriorityColor = useCallback((priority) => {
    const priorityColor = {
      Baixa: 'green.500',
      Media: 'yellow.500',
      Alta: 'red.400',
      'Muito alta': 'red.500',
    }

    return priorityColor[priority]
  }, [])

  const { mutateAsync } = useMutation(async ({ usuario_prioridade, id_chamado }) => {
    const response = await api.post('/site/v2/crm-controle/atualiza-prioridade-chamado', {
      usuario_prioridade: usuario_prioridade,
      id_chamado: id_chamado,
    })

    return response.data
  })

  // Memoize updatePriorityChamado function
  const updatePriorityChamado = useCallback(
    async ({ usuario_prioridade, id_chamado }) => {
      try {
        await mutateAsync({ usuario_prioridade, id_chamado })
        queryClient.invalidateQueries('listarChamadosSistema')
      } catch (error) {
        console.log(error)
        toast({
          title: 'Erro ao atualizar responsável',
          description: 'Ocorreu um erro ao atualizar o responsável do chamado',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        })
      }
    },
    [mutateAsync, queryClient, toast]
  )

  // Memoize listPriority
  const listPriority = useMemo(
    () => [
      {
        name: 'Front',
        value: 'F',
      },
      {
        name: 'Back',
        value: 'BE',
      },
      {
        name: 'Banco',
        value: 'B',
      },
    ],
    []
  )

  // Memoize priority colors function
  const priority = useCallback((priority) => {
    const priorityColor = {
      FRONT: 'blue.500',
      BACK: 'purple.500',
      BANCO: 'orange.500',
      'SEM DEFINICAO': 'gray.500',
    }

    return priorityColor[priority]
  }, [])

  // Memoize responsabilidadeMap for drag and drop
  const responsabilidadeMap = useMemo(
    () => ({
      FRONT: 'F',
      BACK: 'BE',
      BANCO: 'B',
      'SEM DEFINICAO': '',
    }),
    []
  )

  // Memoize handleDragEnd
  const handleDragEnd = useCallback(
    async (result) => {
      const { source, destination, draggableId } = result

      if (!destination) return

      if (destination.droppableId === source.droppableId && destination.index === source.index) return

      if (destination.droppableId !== source.droppableId) {
        // Captura o estado atual antes da operação
        const previousData = queryClient.getQueryData(['listarChamadosSistema'])

        try {
          // Atualização otimista da UI
          queryClient.setQueryData(['listarChamadosSistema'], (oldData) => {
            return oldData.map((item) => {
              if (item.numero_chamado.toString() === draggableId) {
                return {
                  ...item,
                  responsavel: destination.droppableId,
                }
              }
              return item
            })
          })

          toast({
            title: 'Movendo chamado...',
            description: `Chamado movido para ${destination.droppableId}`,
            status: 'info',
            duration: 1000,
            isClosable: true,
            position: 'top-right',
          })

          // Chamada à API em segundo plano
          await updatePriorityChamado({
            usuario_prioridade: responsabilidadeMap[destination.droppableId],
            id_chamado: draggableId,
          })
        } catch (error) {
          // Em caso de erro, restaura o estado anterior
          queryClient.setQueryData(['listarChamadosSistema'], previousData)

          toast({
            title: 'Erro ao mover chamado',
            description: 'Ocorreu um erro e o chamado foi revertido para a posição original',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          })
        }
      }
    },
    [queryClient, toast, responsabilidadeMap, updatePriorityChamado]
  )

  // Memoize columns configuration
  const columns = useMemo(
    () => [
      { id: 'FRONT', title: 'Front', color: 'blue.500' },
      { id: 'BACK', title: 'Back', color: 'purple.500' },
      { id: 'BANCO', title: 'Banco', color: 'orange.500' },
      { id: 'SEM DEFINICAO', title: 'Sem Definição', color: 'gray.500' },
    ],
    []
  )

  // Agrupar dados filtrados por coluna (memoizado)
  const columnData = useMemo(() => {
    if (!filteredData) return {}

    return columns.reduce((acc, column) => {
      acc[column.id] = filteredData.filter((item) => item.responsavel === column.id)
      return acc
    }, {})
  }, [filteredData, columns])

  return (
    <>
      <HeadingPages name="Chamados solicitados" />

      {/* A barra de filtro agora já tem o collapse integrado */}
      <FilterBar onFilterChange={handleFilterChange} />

      <Box
        p="4"
        display="flex"
        justifyContent={{ base: 'flex-start', md: 'space-between' }}
        alignItems="start"
        mb="4"
        flexDir={{ base: 'column', md: 'row' }}
        gap={2}
        width="100%"
      >
        <Badge colorScheme="blue" p={2} fontSize="sm" borderRadius="md">
          Total: {filteredData?.length || 0} chamados
        </Badge>

        <Box maxW="300px">
          <Text fontWeight="bold">Visualizar como:</Text>
          <Select onChange={(e) => setView(e.target.value)} mb="4" value={view}>
            <option value="kanban">Kanban</option>
            <option value="card">Card</option>
            <option value="table">Tabela</option>
          </Select>
        </Box>
      </Box>

      {isLoading && (
        <Center p={8}>
          <Spinner color="blue.500" size="xl" thickness="4px" />
        </Center>
      )}

      {view === 'kanban' && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <KanbanBoard
            columns={columns}
            columnData={columnData}
            setPriorityColor={setPriorityColor}
            updatePriorityChamado={updatePriorityChamado}
            listPriority={listPriority}
            priority={priority}
          />
        </DragDropContext>
      )}

      {view === 'card' && (
        <CardView
          columnData={columnData}
          columns={columns}
          setPriorityColor={setPriorityColor}
          updatePriorityChamado={updatePriorityChamado}
          listPriority={listPriority}
          priority={priority}
        />
      )}

      {view === 'table' && <Tabela data={filteredData} setPriorityColor={setPriorityColor} />}
    </>
  )
}

// Componentizado para evitar renderizações desnecessárias
const CardView = React.memo(({ columnData, columns, setPriorityColor, updatePriorityChamado, listPriority, priority }) => {
  return (
    <Flex flexDirection="column" gap={4} p={4}>
      <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4} fontWeight="bold">
        {columns.map((col) => (
          <Text key={col.id}>{col.title}</Text>
        ))}
      </Grid>

      <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={6}>
        {columns.map((col) => (
          <CardColumn
            key={col.id}
            cards={columnData[col.id] || []}
            columnId={col.id}
            setPriorityColor={setPriorityColor}
            updatePriorityChamado={updatePriorityChamado}
            listPriority={listPriority}
            priority={priority}
          />
        ))}
      </Grid>
    </Flex>
  )
})

// Componente de coluna de cards otimizado com memo
const CardColumn = React.memo(({ cards, columnId, setPriorityColor, updatePriorityChamado, listPriority, priority }) => {
  return (
    <Flex width="100%" direction="column" gap={4} fontWeight="bold" color={priority(columnId)}>
      {cards.map((item) => (
        <Card
          key={item.numero_chamado}
          item={item}
          setPriorityColor={setPriorityColor}
          updatePriorityChamado={updatePriorityChamado}
          listPriority={listPriority}
          priority={priority}
        />
      ))}
    </Flex>
  )
})
